import type { ReactNode } from "react";
import { Box, CircularProgress, Paper, Skeleton, Stack, Typography } from "@mui/material";
import type { LucideIcon } from "lucide-react";

// The loading, empty and error states every operator page shares. The convention:
// - errors: `{error ? <Alert severity="warning">{error}</Alert> : null}` right under the
//   page header (or the section the error belongs to);
// - list pages: `loading ? <CardGridSkeleton/> : items.length ? <CardGrid/> : error ? null
//   : <EmptyState/>`, so a failed load shows the warning alone and never a misleading
//   "nothing here yet";
// - detail pages: `!entity ? (error ? <Alert severity="warning"/> : <PageLoading/>)`, so a
//   failed first load replaces the spinner instead of sitting next to it forever.

/** One column on phones, two on tablets, three on desktops: the card grid of the list pages. */
export const cardGridSx = {
  display: "grid",
  gap: 2,
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }
} as const;

export function CardGrid({ children }: { children: ReactNode }) {
  return <Box sx={cardGridSx}>{children}</Box>;
}

/** Placeholder cards shaped like the grid that will replace them. `sx` overrides the
 *  grid for pages with a layout of their own (the asset library's four columns). */
export function CardGridSkeleton({ count, height, sx = cardGridSx }: { count: number; height: number; sx?: object }) {
  return (
    <Box sx={sx}>
      {Array.from({ length: count }).map((_, index) => <Skeleton key={index} variant="rounded" height={height} />)}
    </Box>
  );
}

/** Placeholder rows for pages that list items in a single column. */
export function ListSkeleton({ count, height, spacing = 1.5 }: { count: number; height: number; spacing?: number }) {
  return (
    <Stack spacing={spacing}>
      {Array.from({ length: count }).map((_, index) => <Skeleton key={index} variant="rounded" height={height} />)}
    </Stack>
  );
}

/** "Nothing here yet": an icon, a bold title and a line on what to do about it.
 *  `compact` is for an empty section inside a page rather than an empty page; `padding`
 *  overrides both for the few pages laid out with their own. Without a title it is the
 *  quiet variant: the description alone as one grey line, under a fainter icon. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  padding
}: {
  icon?: LucideIcon;
  title?: string;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  padding?: number;
}) {
  return (
    <Paper variant="outlined" sx={{ p: padding ?? (compact ? 4 : 6), textAlign: "center" }}>
      {Icon ? <Icon size={34} opacity={title ? 0.5 : 0.4} /> : null}
      {title ? <Typography fontWeight={800} mt={Icon ? 1 : 0}>{title}</Typography> : null}
      {description ? (
        <Typography color="text.secondary" mt={!title && Icon ? 1.5 : 0}>{description}</Typography>
      ) : null}
      {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
    </Paper>
  );
}

/** The detail and editor pages' first load, before there is anything to lay out. */
export function PageLoading() {
  return (
    <Paper variant="outlined" sx={{ p: 5 }}>
      <CircularProgress size={24} />
    </Paper>
  );
}
